import { useAsync } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { fetchQuery, graphql, readInlineData, useRelayEnvironment } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";
import type { Disposable } from "relay-runtime";

import { useFallbackStorageValue, useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { currentArticleAtom } from "scmp-app/lib/current-item";
import { useFlag } from "scmp-app/lib/feature-flag";
import type {
  hooksUseNotificationQueueQuery,
  hooksUseNotificationQueueQuery$variables,
} from "scmp-app/queries/__generated__/hooksUseNotificationQueueQuery.graphql";
import type { hooksUseNotificationReadStatusArticle$key } from "scmp-app/queries/__generated__/hooksUseNotificationReadStatusArticle.graphql";
import type { hooksUseNotificationsSection$key } from "scmp-app/queries/__generated__/hooksUseNotificationsSection.graphql";

import { Notification } from ".";
import { currentNotificationSectionAtom } from "./atoms";

// Breaking news queue: https://staging-scmp-d8.product-web.dev-2.scmp.tech/admin/structure/entityqueue/notification/notification
// Section notification queue: https://staging-scmp-d8.product-web.dev-2.scmp.tech/scmp-entity-subqueue/notification_top
export const useNotifications = () => {
  const currentSection = useAtomValue(currentNotificationSectionAtom);
  const currentSectionData = useMemo(() => {
    if (!currentSection) return;

    const data = readInlineData<hooksUseNotificationsSection$key>(
      graphql`
        fragment hooksUseNotificationsSection on Section @inline {
          ...notificationSection
          entityId
        }
      `,
      currentSection,
    );
    return data;
  }, [currentSection]);

  const currentSectionNotification = useNotificationItem(
    currentSectionData ? `notification_top_${currentSectionData.entityId}` : "",
  );

  const breakingNewsNotification = useNotificationItem("notification");
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const isTickerWidgetEnabled = useFlag("enable_ticker_widget");
  return {
    breakingNewsNotification: isDesktopUp && breakingNewsNotification && isTickerWidgetEnabled && (
      <Notification
        onClose={() => {
          breakingNewsNotification.markRead();
        }}
        onRead={() => {
          breakingNewsNotification.markRead();
        }}
        reference={breakingNewsNotification.item}
        variant={{ type: "breaking" }}
      />
    ),
    currentSectionNotification: isDesktopUp &&
      currentSectionNotification &&
      currentSectionData &&
      isTickerWidgetEnabled && (
        <Notification
          onClose={() => {
            currentSectionNotification.markRead();
          }}
          onRead={() => {
            currentSectionNotification.markRead();
          }}
          reference={currentSectionNotification.item}
          variant={{ reference: currentSectionData, type: "section" }}
        />
      ),
  };
};

const useNotificationItem = (queueName: string) => {
  const item = useFetchNotificationItem(queueName);
  const { hasRead, markRead } = useNotificationReadStatus(queueName, item?.entityId ?? "");
  if (!item) return;
  if (hasRead) return;

  return { item: { ...item, entityId: item.entityId }, markRead };
};

const useFetchNotificationItem = (queueName: string) => {
  const environment = useRelayEnvironment();
  const router = useRouter();
  const disposableReference = useRef<Disposable | null>(null);

  const [{ result }, { execute }] = useAsync(async () => {
    if (!queueName) return;

    const query = graphql`
      query hooksUseNotificationQueueQuery($queueName: String!) {
        queue(filter: { name: $queueName }) {
          items(first: 1) {
            edges {
              node {
                ... on Article {
                  __typename
                  entityId
                }
                ...notificationArticle
              }
            }
          }
        }
      }
    `;
    const variables: hooksUseNotificationQueueQuery$variables = {
      queueName,
    };

    const queryRequest = getRequest(query);
    const queryDescriptor = createOperationDescriptor(queryRequest, variables);

    const response = await fetchQuery<hooksUseNotificationQueueQuery>(
      environment,
      query,
      variables,
    ).toPromise();

    disposableReference.current = environment.retain(queryDescriptor);
    return response;
  });

  // Fetch it once again when route change
  useEffect(() => {
    void execute();
    return () => {
      disposableReference.current?.dispose();
    };
  }, [execute, router.asPath]);

  const item = result?.queue?.items?.edges[0]?.node;

  if (item?.__typename !== "Article") return;
  if (item.entityId === undefined) return;

  return { ...item, entityId: item.entityId };
};

const useNotificationReadStatus = (
  queueName: string,
  notificationItemEntityId: string | undefined,
) => {
  const currentArticle = useAtomValue(currentArticleAtom);
  const currentArticleId = useMemo(() => {
    if (!currentArticle) return;

    const data = readInlineData<hooksUseNotificationReadStatusArticle$key>(
      graphql`
        fragment hooksUseNotificationReadStatusArticle on Article @inline {
          entityId
        }
      `,
      currentArticle,
    );

    return data.entityId;
  }, [currentArticle]);

  // Keep key name as `ticker_${queueName}` for backward compatibility with v1
  const { set: setLastReadNotificationItemEntityId, value: lastReadNotificationItemEntityId } =
    useFallbackStorageValue<string>(`ticker_${queueName}`, {
      parse: value => value,
      stringify: value => value,
    });

  const markRead = useCallback(() => {
    if (!queueName) return;
    if (!notificationItemEntityId) return;
    setLastReadNotificationItemEntityId(notificationItemEntityId);
  }, [notificationItemEntityId, queueName, setLastReadNotificationItemEntityId]);

  useEffect(() => {
    if (!queueName) return;
    if (currentArticleId === undefined) return;
    if (notificationItemEntityId !== currentArticleId) return;
    setLastReadNotificationItemEntityId(currentArticleId);
  }, [currentArticleId, notificationItemEntityId, queueName, setLastReadNotificationItemEntityId]);

  const hasLastRead =
    lastReadNotificationItemEntityId !== undefined &&
    notificationItemEntityId === lastReadNotificationItemEntityId;
  const isReading = currentArticleId !== undefined && currentArticleId === notificationItemEntityId;
  const hasRead = hasLastRead || isReading;

  return { hasRead, markRead };
};
